/**
 * Deferred image loading for images that Chrome's native loading="lazy" would
 * fetch anyway: the browser preloads lazy images up to ~1250px around the
 * viewport, which covers the whole item card image grid, the desktop mega
 * menu and the off-canvas mobile menu (positioned right next to the viewport).
 *
 * Markup: <img class="js-lazyImg" src="<1x1 placeholder>" data-src="<real src>">
 * The real source is set as soon as the image itself becomes visible
 * (IntersectionObserver with a small margin). Hidden containers
 * (display:none, off-screen menus) never trigger a request.
 */

const LAZY_SELECTOR = 'img.js-lazyImg[data-src]';
const ROOT_MARGIN = '300px 0px 300px 0px';

export function loadLazyImage(img) {
    const src = img.getAttribute('data-src');
    if (!src) {
        return;
    }
    img.removeAttribute('data-src');
    img.src = src;
}

export function loadLazyImages(root = document) {
    for (const img of root.querySelectorAll('img[data-src]')) {
        loadLazyImage(img);
    }
}

export function initLazyImages(root = document) {
    const images = root.querySelectorAll(LAZY_SELECTOR);
    if (!images.length) {
        return;
    }
    if (!('IntersectionObserver' in window)) {
        images.forEach(loadLazyImage);
        return;
    }
    const observer = new IntersectionObserver((entries, obs) => {
        for (const entry of entries) {
            if (entry.isIntersecting) {
                loadLazyImage(entry.target);
                obs.unobserve(entry.target);
            }
        }
    }, {rootMargin: ROOT_MARGIN});
    images.forEach(img => observer.observe(img));
}
